import type { Langue } from '../i18n'

/**
 * Étoiles — le motif que le §6 de la conception L10 attend de L8.
 *
 * « Le seul élément visuel typiquement Airbnb qui nous manque. » Deux usages, un composant :
 * lecture et saisie.
 *
 * **SVG, jamais un emoji `★`** (règle 3 du §6) : c'est le seul lot où la tentation est
 * structurelle, et un emoji se remplacerait icône par icône dans chaque écran au moment de L10.
 *
 * **Aucune couleur en dur** (règle 1) : `currentColor` et une variable CSS. La palette de L10
 * changera sans relire ce fichier.
 *
 * **Cibles tactiles ≥ 44 px en saisie** (règle 12) : le bouton porte la taille, pas le SVG.
 */

interface EtoilesProps {
  /** 0 à 5. Les valeurs fractionnaires sont arrondies pour l'affichage. */
  note: number
  langue: Langue
  /** Étiquette accessible : « Propreté », « Réactivité »… */
  libelle: string
  /** Absent en lecture seule. */
  onChange?: (note: number) => void
}

const MAX = 5

export function Etoiles({ note, langue, libelle, onChange }: EtoilesProps) {
  const pleines = Math.round(note)
  const lecture = onChange === undefined

  // Intl, jamais toFixed (regle 9) : « 4,3 » en francais, « ٤٫٣ » en arabe.
  const notation = new Intl.NumberFormat(langue, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(note)

  if (lecture) {
    return (
      <span className="etoiles" role="img" aria-label={`${libelle} : ${notation} / ${MAX}`}>
        {Array.from({ length: MAX }, (_, index) => (
          <Etoile key={index} pleine={index < pleines} />
        ))}
      </span>
    )
  }

  return (
    <span className="etoiles etoiles--saisie" role="radiogroup" aria-label={libelle}>
      {Array.from({ length: MAX }, (_, index) => {
        const valeur = index + 1

        return (
          <button
            key={valeur}
            type="button"
            className="etoile-bouton"
            role="radio"
            aria-checked={valeur === pleines}
            aria-label={new Intl.NumberFormat(langue).format(valeur)}
            onClick={() => onChange(valeur)}
          >
            <Etoile pleine={valeur <= pleines} />
          </button>
        )
      })}
    </span>
  )
}

function Etoile({ pleine }: { pleine: boolean }) {
  return (
    <svg
      className={pleine ? 'etoile etoile--pleine' : 'etoile'}
      viewBox="0 0 24 24"
      width="20"
      height="20"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M12 2.6l2.9 5.9 6.5.9-4.7 4.6 1.1 6.5L12 17.4 6.2 20.5l1.1-6.5L2.6 9.4l6.5-.9z"
        fill={pleine ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  )
}
